import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Box, Stack, Tooltip } from "@mui/material";

import { useAtom, useAtomValue } from "jotai";

import {
  currentVirtualViewerModeAtom,
  VirtualTourViewerMode,
  VirtualTourViewerModeName,
} from "store/atoms/currentVirtualViewerModeAtom";
import { isAddCommentModeActiveAtom } from "store/atoms/isAddCommentModeActive";
import { isDiscardMeasurementModeActiveAtom } from "store/atoms/isDiscardMeasurementModeActive";
import { isMeasurementModeActiveAtom } from "store/atoms/isMeasurementModeActive";
import { isReferenceMeasurementModeActiveAtom } from "store/atoms/isReferenceMeasurementModeActive";
import { isUnityViewerFullScreenAtom } from "store/atoms/isUnityViewerFullScreen";
import { toggleReferenceMeasurementModeIsDisabledAtom } from "store/atoms/toggleReferenceMeasurementModeIsDisabled";
import { isInstructionSplashScreenOpenAtom } from "store/atoms/virtualTourViewer/isInstructionSplashScreenOpen";

import commentIcon from "../../../../assets/images/icons/vtToolbar/CommentIconTransparent 1.png";
import cubeIcon from "../../../../assets/images/icons/vtToolbar/Cube.png";
import floorplanIcon from "../../../../assets/images/icons/vtToolbar/Floorplan.png";
import footstepIcon from "../../../../assets/images/icons/vtToolbar/Footsteps.png";
import trashIcon from "../../../../assets/images/icons/vtToolbar/image 3.png";
import measureIcon from "../../../../assets/images/icons/vtToolbar/image 4.png";
import rulerIcon from "../../../../assets/images/icons/vtToolbar/Ruler 1.png";
import RouteIcon from "@mui/icons-material/Route";
import ReferenceMeasurementInformationBox from "../ReferenceMeasurementInformationBox";

import {
  styles,
  commonStyles,
  miniCommonStyles,
  cursorStyleOnReferenceMeasurementModeButton,
} from "./style";
import { isPathActiveAtom } from "store/atoms/isPathActiveAtom";
import useCurrentUserProfile from "hooks/useCurrentUserProfile";
import { useTurnOffVtourManualOnStartMutation } from "api/userProfiles/useTurnOffVtourManualOnStartMutation";
import { useLoading } from "contexts/LoadingContext";

const VirtualTourViewerMainToolbar = () => {
  const { fullscreen } = useLoading();
  const [currentVTourViewerMode, setCurrentVTourViewerMode] = useAtom(
    currentVirtualViewerModeAtom
  );
  const [isMeasurementModeActive, setIsMeasurementModeActive] = useAtom(
    isMeasurementModeActiveAtom
  );
  const [
    isReferenceMeasurementModeActive,
    setIsReferenceMeasurementModeActive,
  ] = useAtom(isReferenceMeasurementModeActiveAtom);
  const toggleReferenceMeasurementModeIsDisabled = useAtomValue(
    toggleReferenceMeasurementModeIsDisabledAtom
  );
  const [isDiscardMeasurementModeActive, setIsDiscardMeasurementModeActive] =
    useAtom(isDiscardMeasurementModeActiveAtom);

  const [isPathActive,setIsPathActive] = useAtom(isPathActiveAtom);

  const [isAddCommentModeActive, setIsAddCommentModeActive] = useAtom(
    isAddCommentModeActiveAtom
  );
  
  const { userProfile } = useCurrentUserProfile();

  const shouldInstructionSplashScreen = userProfile?.metadata?.showVirtualTourManual ?? true;

  const { turnOffVtourManualOnStart } = useTurnOffVtourManualOnStartMutation();

  const [isInstructionSplashScreenOpen, setIsInstructionSplashScreenOpen] =
    useAtom(isInstructionSplashScreenOpenAtom);
  const vTourModes: {
    name: VirtualTourViewerModeName;
    label: string;
    icon: string;
    onClick?: () => void;
  }[] = [
      {
        name: "Orbit",
        label: "Orbit Controls",
        icon: cubeIcon,
        onClick: () => setCurrentVTourViewerMode({
          name: "Orbit",
          calledFrom: "main-toolbar",
        }),
      },
      {
        name: "Floorplan",
        label: "Floor Plan",
        icon: floorplanIcon,
        onClick: () => setCurrentVTourViewerMode({
          name: "Floorplan",
          calledFrom: "main-toolbar",
        }),
      },
      {
        name: "VirtualTour",
        label: "Walk Through",
        icon: footstepIcon,
        onClick: () => setCurrentVTourViewerMode({
          name: "VirtualTour",
          calledFrom: "main-toolbar",
        }),
      },
    ];

  const toggleMeasurementMode = () => {
    if (!isMeasurementModeActive) {
      // if we are switching measurement mode on, then switch off comment mode
      setIsAddCommentModeActive(false);
    }
    setIsMeasurementModeActive(!isMeasurementModeActive);
  };

  const toggleReferenceMeasurementMode = () => {
    if (toggleReferenceMeasurementModeIsDisabled) return;
    setIsReferenceMeasurementModeActive(!isReferenceMeasurementModeActive);
  };

  const toggleDiscardMeasurementMode = () =>
    setIsDiscardMeasurementModeActive(!isDiscardMeasurementModeActive);

  const toggleAddCommentMode = () => {
    if (!isAddCommentModeActive) {
      // if we are switching comment mode on, then switch off measurement mode
      setIsMeasurementModeActive(false);
    }
    setIsAddCommentModeActive(!isAddCommentModeActive);
  };

  const toggleInstructionSplashScreen = () => {
    //console.log("shouldInstructionSplashScreen ",shouldInstructionSplashScreen,isInstructionSplashScreenOpen);
    setIsInstructionSplashScreenOpen(!isInstructionSplashScreenOpen);
    if (!shouldInstructionSplashScreen) {
      turnOffVtourManualOnStart();
    }
  };

  return (
    <Stack sx={styles.toolbarContainer}>
      <Stack sx={styles.toolbarminiWrapper}>
        {vTourModes.map((mode, index) => (
          <Tooltip title={mode.label} key={index} placement="left">
            <Stack
              sx={commonStyles(
                fullscreen,
                currentVTourViewerMode.name === mode.name
              )}
              onClick={mode.onClick}
            >
              <img src={mode.icon} alt={mode.name} width="50%" height="50%" />
            </Stack>
          </Tooltip>
        ))}
      </Stack>
      <Stack sx={styles.toolbarminiWrapper}>
        <Tooltip
          title="Measurement"
          placement="right"
          PopperProps={{
            style: {
              zIndex: 10,
            },
          }}
        >
          <Stack
            sx={commonStyles(fullscreen, isMeasurementModeActive)}
            onClick={toggleMeasurementMode}
          >
            <img src={rulerIcon} alt="measureIcon" width="50%" height="50%" />
          </Stack>
        </Tooltip>
        <Stack
          sx={{
            zIndex: 11,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            position: "absolute",
            left: "120%",
          }}
        >
          <Box sx={styles.refMeasurementsInformationBox}>
            <ReferenceMeasurementInformationBox />
          </Box>
          <Tooltip title="Create Reference Measurement">
            <Stack
              sx={{
                ...miniCommonStyles(
                  fullscreen,
                  !!isReferenceMeasurementModeActive,
                  isMeasurementModeActive
                ),
                ...cursorStyleOnReferenceMeasurementModeButton(
                  toggleReferenceMeasurementModeIsDisabled
                ),
              }}
              onClick={toggleReferenceMeasurementMode}
            >
              <img
                src={measureIcon}
                alt="referenceMeasureIcon"
                style={{
                  objectFit: "contain",
                  width: "60%",
                  aspectRatio: "1/1",
                }}
              />
            </Stack>
          </Tooltip>
          <Tooltip title="Delete Measurement">
            <Stack
              sx={miniCommonStyles(
                fullscreen,
                isDiscardMeasurementModeActive,
                isMeasurementModeActive,
                true
              )}
              onClick={toggleDiscardMeasurementMode}
            >
              <img
                src={trashIcon}
                alt="measureIcon"
                style={{
                  objectFit: "contain",
                  width: "50%",
                  aspectRatio: "1/1",
                }}
              />
            </Stack>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack sx={styles.toolbarminiWrapper}>
        <Tooltip title="Add Comment" placement="left">
          <Stack
            sx={commonStyles(fullscreen, isAddCommentModeActive)}
            onClick={toggleAddCommentMode}
          >
            <img src={commentIcon} alt="commentIcon" width="50%" height="50%" />
          </Stack>
        </Tooltip>
      </Stack>
      <Stack sx={styles.toolbarminiWrapper}>
      <Tooltip title="Show Path" placement="left">
  <Stack
    sx={commonStyles(fullscreen, isPathActive)} // Update to reflect active state
    onClick={() => {
      setIsPathActive(!isPathActive);
    }}
  >
    <RouteIcon sx={{ fontSize: 25 }} />
  </Stack>
</Tooltip>
        <Tooltip title="Show Instruction Manual" placement="left">
          <Stack
            sx={commonStyles(fullscreen, (isInstructionSplashScreenOpen && shouldInstructionSplashScreen))}
            onClick={toggleInstructionSplashScreen}
          >
            <QuestionMarkIcon />
          </Stack>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default VirtualTourViewerMainToolbar;
